import { useState } from "react";
import { PATHWAY } from "@/constants/testIds";
import { buildApiPreview } from "@/lib/receipt";

export default function ReceiptModal({ receipt, onClose, onDownload }) {
  const [view, setView] = useState("receipt"); // receipt | timeline | api
  const [explain, setExplain] = useState(false);

  if (!receipt) return null;
  const api = view === "api" ? buildApiPreview(receipt) : null;

  const close = () => {
    onClose();
    setView("receipt");
    setExplain(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/85 px-4 py-10 sm:py-16"
      role="dialog"
      aria-modal="true"
      data-testid={PATHWAY.receiptModal}
      onClick={close}
    >
      <div
        className="w-full max-w-3xl border hairline bg-[#0f0f0f] receipt-grain"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b hairline px-6 py-5">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-neutral-500">
              {view === "api"
                ? "Illustrative runtime API"
                : view === "timeline"
                ? "Illustrative timeline · sponsor showcase"
                : "Illustrative receipt · sponsor showcase"}
            </div>
            <div className="mt-2 font-serif text-2xl text-neutral-100">
              {receipt.product}
            </div>
            <div className="mt-1 font-mono text-xs text-neutral-500">
              {receipt.schema}
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            data-testid={PATHWAY.receiptClose}
            className="font-mono text-xs uppercase tracking-[0.2em] text-neutral-400 hover:text-amber-500 transition-colors"
            aria-label="Close receipt"
          >
            Close ×
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b hairline">
          <TabButton
            active={view === "receipt"}
            onClick={() => setView("receipt")}
            testId={PATHWAY.receiptTabReceipt}
          >
            Receipt
          </TabButton>
          <TabButton
            active={view === "timeline"}
            onClick={() => setView("timeline")}
            testId={PATHWAY.receiptTabTimeline}
          >
            Timeline
          </TabButton>
          <TabButton
            active={view === "api"}
            onClick={() => setView("api")}
            testId={PATHWAY.receiptViewApi}
          >
            View API
          </TabButton>
        </div>

        {/* Verification stamp */}
        <div
          data-testid={PATHWAY.receiptVerificationState}
          className="flex flex-wrap items-center justify-between gap-3 border-b hairline px-6 py-4"
        >
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-neutral-500">
              verification_state
            </div>
            <div className="mt-2 inline-block border border-[#7F1D1D] px-3 py-1.5 font-mono text-xs tracking-wider text-[#dc2626]">
              {receipt.verification_state}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setExplain((v) => !v)}
            data-testid={PATHWAY.receiptExplainButton}
            className="border hairline px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-neutral-200 transition-colors hover:border-amber-700 hover:text-amber-500"
          >
            {explain ? "Hide explanation" : "Explain this decision"}
          </button>
        </div>

        {/* Explain panel (toggleable) */}
        {explain && (
          <ExplainPanel explainData={receipt.explain} />
        )}

        {view === "receipt" && <ReceiptBody receipt={receipt} />}
        {view === "timeline" && <TimelineBody timeline={receipt.timeline} />}
        {view === "api" && <ApiBody api={api} />}

        {/* Footer */}
        <div className="flex flex-col gap-3 border-t hairline px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-neutral-500">
            KATLAS — Runtime Operational Governance for AI-enabled multi-party
            workflows
          </div>
          <button
            type="button"
            onClick={onDownload}
            data-testid={PATHWAY.receiptDownloadJson}
            className="border border-amber-700 bg-amber-700 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors hover:bg-amber-600"
          >
            Download JSON
          </button>
        </div>
      </div>
    </div>
  );
}

function TabButton({ children, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`flex-1 border-r hairline px-5 py-3 text-left font-mono text-[10px] uppercase tracking-[0.24em] transition-colors last:border-r-0 ${
        active
          ? "bg-[#1a1a1a] text-amber-500"
          : "text-neutral-500 hover:text-neutral-200"
      }`}
    >
      {children}
    </button>
  );
}

function ExplainPanel({ explainData }) {
  if (!explainData) return null;
  const a = explainData.authority_chain_summary || {};
  return (
    <div
      data-testid={PATHWAY.receiptExplainPanel}
      className="border-b hairline bg-[#101010] px-6 py-5"
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-amber-500">
        Explain this decision
      </div>
      <div className="mt-4 grid grid-cols-1 gap-x-8 gap-y-3 md:grid-cols-2">
        <ExplainRow label="Decision" value={explainData.decision} />
        <ExplainRow label="Reason" value={explainData.reason} />
        <ExplainRow
          label="Authority chain"
          value={
            <span>
              <span className="text-amber-500">
                {a.verified ? "Verified" : "Unverified"}
              </span>
              <span className="text-neutral-500"> · </span>
              <span className="text-[#dc2626]">
                {a.escalated ? "Escalated" : "Not escalated"}
              </span>
            </span>
          }
        />
        <ExplainRow label="Receipt" value={explainData.receipt_status} />
      </div>
      <div className="mt-5 border-t hairline pt-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-neutral-500">
          Evidence used
        </div>
        <ul className="mt-3 grid gap-1 sm:grid-cols-2">
          {(explainData.evidence_used || []).map((e) => (
            <li
              key={e}
              className="font-mono text-[12px] text-neutral-200 before:mr-2 before:content-['—']"
            >
              {e}
            </li>
          ))}
        </ul>
      </div>
      <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-neutral-500">
        Illustrative explainability surface · synthetic
      </div>
    </div>
  );
}

function ExplainRow({ label, value }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-neutral-500">
        {label}
      </div>
      <div className="mt-1 text-[14px] text-neutral-100">{value}</div>
    </div>
  );
}

function ReceiptBody({ receipt }) {
  return (
    <div className="px-6 py-6 font-mono text-[13px] leading-relaxed text-neutral-200">
      <ReceiptRow label="receipt_id" value={receipt.receipt_id} mono />
      <ReceiptRow label="timestamp" value={receipt.timestamp} mono />
      <ReceiptRow label="policy_version" value={receipt.policy_version} mono />
      <ReceiptRow label="receipt_category" value={receipt.receipt_category || "illustrative_preview"} mono />
      <ReceiptRow label="runtime_status_observed" value={receipt.runtime_status_observed || "unknown"} mono />
      <ReceiptRow label="mode" value={receipt.mode} />
      <ReceiptRow label="sponsor_theme" value={receipt.sponsor_theme} />
      <ReceiptRow label="risk_scenario" value={receipt.risk_scenario} />

      <ReceiptAuthorityChain chain={receipt.authority_chain} />
      <ReceiptParticipants participants={receipt.participants} />

      <ReceiptList
        label="evidence_requested"
        items={receipt.evidence_requested}
      />
      <ReceiptList
        label="evidence_withheld"
        items={receipt.evidence_withheld}
      />
      <ReceiptRow label="evidence_hash" value={receipt.evidence_hash} mono />
      <ReceiptRow label="ai_use_boundary" value={receipt.ai_use_boundary} />
      <ReceiptRow label="decision" value={receipt.decision} />
      <ReceiptRow
        label="authorised_decision"
        value={receipt.authorised_decision}
      />
      <ReceiptRow
        label="suggested_next_step"
        value={receipt.suggested_next_step}
      />

      {receipt.intake && (
        <div className="mt-6 border-t hairline pt-5">
          <div className="mb-3 text-[10px] uppercase tracking-[0.28em] text-neutral-500">
            intake
          </div>
          <ReceiptRow
            label="organisation"
            value={receipt.intake.organisation || "—"}
          />
          <ReceiptRow
            label="insurance_line"
            value={receipt.intake.insurance_line || "—"}
          />
          <ReceiptList
            label="target_audiences"
            items={receipt.intake.target_audiences}
          />
          <ReceiptList
            label="desired_outputs"
            items={receipt.intake.desired_outputs}
          />
          <ReceiptRow
            label="indicative_budget_band"
            value={receipt.intake.indicative_budget_band || "—"}
          />
        </div>
      )}
    </div>
  );
}

function TimelineBody({ timeline }) {
  return (
    <div
      data-testid={PATHWAY.receiptTimelinePanel}
      className="px-6 py-6 font-mono text-[13px] leading-relaxed"
    >
      <ol className="border-l hairline">
        {(timeline || []).map((t, i) => (
          <li
            key={`${t.time}-${i}`}
            data-testid={PATHWAY.receiptTimelineEvent(i + 1)}
            className="relative grid grid-cols-[80px_1fr] gap-4 pl-6 pb-5 last:pb-0"
          >
            <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 bg-amber-600" />
            <span className="text-amber-300">{t.time}</span>
            <span className="text-neutral-100">{t.event}</span>
          </li>
        ))}
      </ol>
      <div className="mt-6 border-t hairline pt-4 text-[10px] uppercase tracking-[0.22em] text-neutral-500">
        Illustrative timeline · synthetic
      </div>
    </div>
  );
}

function ApiBody({ api }) {
  return (
    <div
      data-testid={PATHWAY.receiptApiPanel}
      className="px-6 py-6 font-mono text-[12px] leading-relaxed text-neutral-200"
    >
      <div className="text-[10px] uppercase tracking-[0.28em] text-neutral-500">
        request
      </div>
      <pre className="mt-3 overflow-x-auto border hairline bg-[#0a0a0a] p-4 text-amber-300">
{api.request}
      </pre>

      <div className="mt-6 text-[10px] uppercase tracking-[0.28em] text-neutral-500">
        response
      </div>
      <pre className="mt-3 overflow-x-auto border hairline bg-[#0a0a0a] p-4 text-neutral-200">
{api.response}
      </pre>

      <p className="mt-6 border-l-2 border-[#7F1D1D] pl-4 text-[12px] italic leading-relaxed text-neutral-400">
        {api.note}
      </p>
    </div>
  );
}

function ReceiptRow({ label, value, mono }) {
  return (
    <div className="grid grid-cols-1 gap-1 border-b hairline py-2 sm:grid-cols-[200px_1fr] sm:gap-4">
      <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500">
        {label}
      </div>
      <div className={`break-words text-neutral-100 ${mono ? "text-amber-300" : ""}`}>
        {value}
      </div>
    </div>
  );
}

function ReceiptList({ label, items }) {
  return (
    <div className="grid grid-cols-1 gap-1 border-b hairline py-2 sm:grid-cols-[200px_1fr] sm:gap-4">
      <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500">
        {label}
      </div>
      <ul className="space-y-1 text-neutral-100">
        {(items || []).map((i) => (
          <li key={i} className="before:mr-2 before:content-['—']">
            {i}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ReceiptAuthorityChain({ chain }) {
  if (!chain || !chain.length) return null;
  return (
    <div className="grid grid-cols-1 gap-1 border-b hairline py-2 sm:grid-cols-[200px_1fr] sm:gap-4">
      <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500">
        authority_chain
      </div>
      <ol className="space-y-2">
        {chain.map((c, i) => (
          <li key={i} className="grid grid-cols-[24px_1fr] gap-3">
            <span className="text-neutral-500">{String(i + 1).padStart(2, "0")}</span>
            <div>
              <div className="text-neutral-100">
                <span className="text-amber-400">{c.actor}</span>
                <span className="text-neutral-500"> · </span>
                <span>{c.action}</span>
              </div>
              {c.note && (
                <div className="text-[11px] text-neutral-500">{c.note}</div>
              )}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function ReceiptParticipants({ participants }) {
  if (!participants || !participants.length) return null;
  return (
    <div className="grid grid-cols-1 gap-1 border-b hairline py-2 sm:grid-cols-[200px_1fr] sm:gap-4">
      <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500">
        participants
      </div>
      <ul className="space-y-1 text-neutral-100">
        {participants.map((p, i) => (
          <li key={i}>
            <span className="text-amber-400">{p.role}</span>
            <span className="text-neutral-500"> · {p.attribution}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
